import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';

import { ListResponse } from '../../../../../models/list-response';
import {
  Challenge,
  ChallengeFilters,
  ChallengeStatus,
  getChallengeStatusDisplay,
  loadChallengeFiltersFromGetParams
} from '../../../../../models/challenge';
import { AccountService } from '../../../../../services/account/account.service';
import { ChallengeService } from '../../../../../services/challenge/challenge.service';
import { addFiltersToGetParams } from '../../../../../utils';
import { LocalStorage } from 'angular-web-storage';

@Component({
  selector: 'app-challenges',
  templateUrl: './challenges.component.html',
  styleUrls: ['./challenges.component.css']
})
export class ChallengesComponent implements OnInit {
  public pageNumber = 1;
  // Giving this a project-unique name ensures it is not shared with other saved
  // pageSize variables.
  @LocalStorage() public pageSizeChallenges;
  public filters: ChallengeFilters = {
    search: '',
    status: null
  };

  public filtersChangedSubject = new Subject<null>();

  public challengeList: ListResponse<Challenge>;

  public loadingChallenges = true;

  public serverError: number = null;

  // Make browser history available to the template.
  public history: History;

  // The number of list requests. Used to ensure that only the most recent
  // request is displayed, in the event of concurrent requests (due to,
  // e.g., filtering).
  private listRequestCount = 0;

  public readonly ChallengeStatus = ChallengeStatus;
  public readonly getChallengeStatusDisplay = getChallengeStatusDisplay;

  constructor(
    public accountService: AccountService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private challengeService: ChallengeService
  ) {}

  ngOnInit() {
    if (!this.pageSizeChallenges) {
      this.pageSizeChallenges = 10;
    }
    // This is needed in order for the pageSize to be updated when the html
    // select is first changed.
    // noinspection SillyAssignmentJS
    this.pageSizeChallenges = this.pageSizeChallenges;

    // Make browser history available to the template.
    this.history = history;

    // Handle changes to the filters by reloading the challenges,
    // subject to debounce.
    this.filtersChangedSubject.pipe(
      map(() => { this.loadingChallenges = true; }),
      // Avoid rapid requests by waiting 300ms before applying the filters.
      debounceTime(300)
    ).subscribe(() => {
      this.loadChallenges();
      addFiltersToGetParams(
        this.filters,
        this.router,
        this.activatedRoute
      );
    });

    // Load initial query parameters from URL if present.
    this.filters = loadChallengeFiltersFromGetParams(this.activatedRoute);
    if (!this.filters.status) { this.filters.status = null; }

    this.loadChallenges();
  }

  /**
   * Whether a non-zero number of results have been loaded.
   */
  public resultsPresent(): boolean {
    return (
      this.challengeList &&
      this.challengeList.results &&
      (this.challengeList.results.length > 0)
    );
  }

  /**
   * Update the current page number to the given value and load the page of
   * results.
   *
   * @param newPageNumber - The new page number.
   */
  public setPage(newPageNumber: number) {
    this.pageNumber = newPageNumber;
    this.filtersChangedSubject.next();
  }

  /**
   * Get the number of the last page of results.
   */
  public lastPageNumber() {
    if (!this.challengeList) { return 1; }
    return Math.ceil(this.challengeList.count / this.pageSizeChallenges);
  }

  private loadChallenges() {
    this.loadingChallenges = true;

    // Increment the request count and save a local copy.
    this.listRequestCount++;
    const thisRequestCount = this.listRequestCount;

    this.challengeService
      .getChallenges(
        this.pageNumber,
        this.pageSizeChallenges,
        this.filters
      )
      .then(response => {
        // Only update the list if there has not been a more recent request
        // initiated.
        if (thisRequestCount === this.listRequestCount) {
          this.challengeList = response;
        }
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        if (thisRequestCount === this.listRequestCount) {
          this.loadingChallenges = false;
        }
      });
  }
}
